// @ts-expect-error React import required to render in Jira SSR
import React from 'react';
import {
	type FrontendPlacementMessage,
	type AnyFrontendStage,
} from '@post-office/message-maker-browser/types';

import { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';

import FadeIn from '@atlaskit/motion/fade-in';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import { Text } from '@atlaskit/primitives/compiled/text';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import { useMessageEvent } from '@post-office/message-lifecycle';

import { noValidation } from '@post-office/message-maker-browser/message-helpers';

import { PlacementFrontend } from '@post-office/browser--rovo-chat/rovo-chat-long-running-task-spotlight';

import { useMountToHtmlElement } from '@post-office/components--post-office-team';

import { type MessageTemplateContext } from '../../../index';

import { messages } from './messages';

import type { CreatePlacementMessageConfig } from '@post-office/message-template-contracts/placement-v2';

import { SpotlightHeadline } from '@atlaskit/spotlight/headline';

import { SpotlightHeader } from '@atlaskit/spotlight/header';

import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';

import { SpotlightControls } from '@atlaskit/spotlight/controls';

import { SpotlightCard } from '@atlaskit/spotlight/card';

import { SpotlightBody } from '@atlaskit/spotlight/body';

import { PopoverTarget } from '@atlaskit/spotlight/popover-target';

import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';

import { PopoverContent } from '@atlaskit/spotlight/popover-content';

const SPOTLIGHT_TARGET_SELECTOR = '[data-spotlight-target="rovo-chat-ask-rovo-button"]';

type PlacementRenderProps = {
	onDismiss: () => void;
	context?: MessageTemplateContext;
};

// This is the Message Frontend. In this Placement we have render.Component which takes a React Component which will render your message in the Placement.
export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: AnyFrontendStage;
}> = PlacementFrontend.createMessageFrontend({
	render: {
		validate: noValidation<PlacementRenderProps>(),
		Component: ({ onDismiss }: PlacementRenderProps) => {
			const { onClosed } = usePostOfficeUserInteractions();
			const { messageDismissed } = useMessageEvent();
			const { formatMessage } = useIntl();
			const [isVisible, setIsVisible] = useState(true);

			const renderInline = useMountToHtmlElement({
				selectors: SPOTLIGHT_TARGET_SELECTOR,
				as: 'inline',
				position: 'inside',
			});

			const handleDismiss = useCallback(async () => {
				setIsVisible(false);
				await messageDismissed();
				void onClosed({
					interactionIdentifier: 'rovo-chat-long-running-task-spotlight-dismiss',
					additionalMetadata: {
						maxIntroSuppressionSource: 'spotlight-dismiss',
					},
				});

				onDismiss();
			}, [messageDismissed, onClosed, onDismiss]);

			return (
				<div>
					{renderInline(
						<PopoverProvider>
							<PopoverTarget>
								<div />
							</PopoverTarget>
							<PopoverContent
								dismiss={handleDismiss}
								// `bottom-start` places the card below the button with the caret anchored to
								// the right side of the card (ADS layout maps `bottom-start` -> caret at end).
								// Combined with negative skidding, this keeps the card inside the viewport
								// while pointing the arrow up-and-right at the Ask Rovo button.
								placement="bottom-start"
								isVisible={isVisible}
								offset={[-14, 24]}
							>
								{(() => {
									const spotlightCard = (
										<SpotlightCard>
											<SpotlightHeader>
												<SpotlightHeadline>{formatMessage(messages.headline)}</SpotlightHeadline>
												<SpotlightControls>
													{/*
													 * Inline style block + testId override: the spotlight popover renders in the
													 * browser top-layer (outside our React subtree), and a Confluence parent
													 * container (`._7dirvuon`) applies
													 * `button { background-color: var(--ds-surface) }` to every nested button,
													 * including ADS's SpotlightDismissControl. We can't reach the button via
													 * a wrapper element (top-layer breaks cascade), so we use `!important` on
													 * a testId-scoped global rule. This is targeted (only our dismiss button)
													 * and unavoidable given the host stylesheet's overly-broad selector.
													 */}
													<style>{`
														/*
														 * Use ADS tokens via their CSS custom properties so the icon and hover
														 * states automatically flip in dark mode (since the underlying spotlight
														 * card surface, color.background.neutral.bold, also flips).
														 *   - color.text.inverse pairs with color.background.neutral.bold
														 *   - color.background.neutral.subtle.hovered/pressed work for the wash
														 */
														button[data-testid="rovo-chat-long-running-task-spotlight-dismiss"] {
															background-color: transparent !important;
															color: var(--ds-text-inverse) !important;
														}
														button[data-testid="rovo-chat-long-running-task-spotlight-dismiss"]:hover {
															background-color: var(--ds-background-neutral-subtle-hovered) !important;
														}
														button[data-testid="rovo-chat-long-running-task-spotlight-dismiss"]:active {
															background-color: var(--ds-background-neutral-subtle-pressed) !important;
														}
														button[data-testid="rovo-chat-long-running-task-spotlight-dismiss"] svg {
															color: var(--ds-text-inverse) !important;
															fill: currentColor !important;
														}
													`}</style>
													<SpotlightDismissControl
														onClick={handleDismiss}
														testId="rovo-chat-long-running-task-spotlight-dismiss"
														// Spotlight is system-triggered (Post Office message), not user-action;
														// per ADS guidance, autoFocus should be false in that case.
														autoFocus={false}
													/>
												</SpotlightControls>
											</SpotlightHeader>
											<SpotlightBody>
												<Text>{formatMessage(messages.body)}</Text>
											</SpotlightBody>
										</SpotlightCard>
									);
									return fg('platform-dst-motion-uplift-spotlight') ? (
										spotlightCard
									) : (
										<FadeIn entranceDirection="bottom">
											{(props) => <div {...props}>{spotlightCard}</div>}
										</FadeIn>
									);
								})()}
							</PopoverContent>
						</PopoverProvider>,
					)}
				</div>
			);
		},
	},
});
